import { useState } from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { TextField } from '../form/TextField';

export interface MagicLinkPromptProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (email: string) => Promise<void> | void;
  onSkip: () => void;
  loading?: boolean;
}

/**
 * Modal that appears once per session when the user starts answering form
 * questions. Captures an email so the backend can generate and email a
 * magic-link resume URL (§6.5 of docs/property/PRD.md).
 *
 * Users can skip — their answers still autosave to sessionStorage, they
 * just can't resume on a different device or browser.
 */
export function MagicLinkPrompt({
  open,
  onClose,
  onSubmit,
  onSkip,
  loading,
}: MagicLinkPromptProps) {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function handle(e: React.FormEvent) {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Enter a valid email address.');
      return;
    }
    setError(null);
    try {
      await onSubmit(email);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Could not save draft — try again.'
      );
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Save your progress by email">
      <div className="flex flex-col gap-6 max-w-md">
        <div className="flex flex-col gap-2">
          <span className="font-mono text-[0.625rem] uppercase tracking-[0.125em] text-ink-subtle">
            &gt; SAVE_PROGRESS
          </span>
          <p className="font-display text-[0.9375rem] text-ink-muted leading-relaxed">
            We'll send a one-time link so you can pick up where you left
            off on any device. Your answers autosave here on this device
            either way.
          </p>
        </div>

        <form onSubmit={handle} className="flex flex-col gap-4">
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError(null);
            }}
            error={error ?? undefined}
            hint="We only use this to send your resume link and to follow up on your submission."
          />

          <div className="flex flex-wrap gap-2 pt-1">
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? 'Sending…' : 'Send me the link'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setError(null);
                onSkip();
              }}
              disabled={loading}
            >
              Skip for now
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
